import { MetricCard } from "./MetricCard.jsx";
import { HorizontalBar } from "./HorizontalBar.jsx";

const C = { bg: "#111", border: "#1e1e1e", accent: "#e8700a", text: "#f5f5f5", dim: "#666", blue: "#3b82f6", green: "#22c55e" };
const GAP = 8;

function fmt(n, currency) {
  if (n == null) return "—";
  const sym = currency === "USD" ? "US$" : "$";
  return `${sym}${Math.round(n).toLocaleString("es-AR")}`;
}

function Card({ children, style }) {
  return <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 18px", ...style }}>{children}</div>;
}

function CardTitle({ children }) {
  return <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", color: C.dim, textTransform: "uppercase", marginBottom: 12 }}>{children}</div>;
}

function MedioPago({ nombre, total, pct, currency }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 12, color: C.text }}>{nombre}</span>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.accent }}>{fmt(total, currency)}</div>
        <div style={{ fontSize: 10, color: C.dim }}>{pct}%</div>
      </div>
    </div>
  );
}

function TurnoCard({ turno, currency, label }) {
  if (!turno) return <Card><CardTitle>{label}</CardTitle><div style={{ color: C.dim, fontSize: 12 }}>Sin datos</div></Card>;
  const medios = turno.medios_de_pago || [];
  return (
    <Card>
      <CardTitle>{turno.label || label}</CardTitle>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>{fmt(turno.total, currency)}</div>
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 12 }}>{turno.transacciones} transacciones</div>
      {medios.length > 0
        ? medios.map((m, i) => <MedioPago key={i} {...m} currency={currency} />)
        : <div style={{ color: C.dim, fontSize: 11 }}>Medios de pago no disponibles</div>}
    </Card>
  );
}

function SkeletonRow({ cols }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: GAP }}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} style={{ height: 90, background: "#161616", borderRadius: 10, animation: "pulse 1.5s ease-in-out infinite" }} />
      ))}
    </div>
  );
}

export function ResultadosView({ summary, expenses, currency, loading }) {
  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", gap: GAP }}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      <SkeletonRow cols={4} /><SkeletonRow cols={2} /><SkeletonRow cols={2} /><SkeletonRow cols={3} />
    </div>
  );

  const s = summary || {};
  const turnos = s.ventas_por_turno || {};
  const exp = expenses;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: GAP }}>

      {/* Fila 1: 4 métricas */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: GAP }}>
        <MetricCard label="Facturación" value={fmt(s.facturacion_total, currency)} sub={`${s.transacciones ?? "—"} transacciones`} />
        <MetricCard label="Gastos" value={fmt(s.gastos_totales, currency)} sub={s.gastos_totales == null ? "No disponible" : undefined} accent={s.gastos_totales != null} />
        <MetricCard label="Utilidad" value={fmt(s.utilidad, currency)} sub={s.margen_pct != null ? `Margen ${s.margen_pct}%` : "No disponible"} />
        <MetricCard label="Caja final" value={fmt(s.caja_final, currency)} />
      </div>

      {/* Fila 2: Turnos */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: GAP }}>
        <TurnoCard turno={turnos.manana} currency={currency} label="Mañana" />
        <TurnoCard turno={turnos.tarde}  currency={currency} label="Tarde" />
      </div>

      {/* Fila 3: Gastos por categoría + Dividendos (dividendos sólo si no es null) */}
      {exp && exp.disponible ? (
        <div style={{ display: "grid", gridTemplateColumns: exp.dividendos != null ? "1fr 1fr" : "1fr", gap: GAP }}>
          <Card>
            <CardTitle>Estructura de costos</CardTitle>
            <HorizontalBar
              items={(exp.categorias || []).map(c => ({ label: c.categoria, value: c.total, pct: c.pct }))}
              unit="$"
            />
          </Card>
          {exp.dividendos != null && (
            <Card>
              <CardTitle>Dividendos</CardTitle>
              {exp.dividendos.length > 0
                ? exp.dividendos.map((d, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{d.socio}</div>
                        <div style={{ fontSize: 11, color: C.dim }}>{d.pct}%</div>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>{fmt(d.monto, currency)}</div>
                    </div>
                  ))
                : <div style={{ color: C.dim, fontSize: 12 }}>Sin dividendos configurados para este local</div>}
            </Card>
          )}
        </div>
      ) : exp && !exp.disponible ? (
        <Card><div style={{ color: C.dim, fontSize: 12, textAlign: "center", padding: "24px 0" }}>Datos de gastos no disponibles para este POS</div></Card>
      ) : null}

      {/* Fila 4: Proveedores por categoría (Alimentos, Bebidas, Servicios) */}
      {exp && exp.disponible && exp.categorias && exp.categorias.length > 0 && (() => {
        const TARGET = ["Alimentos", "Bebidas", "Servicios"];
        const cols = TARGET.map(cat => exp.categorias.find(c => c.categoria === cat)).filter(Boolean);
        if (cols.length === 0) return null;
        return (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols.length}, 1fr)`, gap: GAP }}>
            {cols.map((cat, i) => (
              <Card key={i}>
                <CardTitle>{cat.categoria} por proveedor</CardTitle>
                <HorizontalBar
                  items={(cat.proveedores || []).slice(0, 6).map(p => ({ label: p.nombre, value: p.total }))}
                  unit="$"
                  showPct={false}
                />
              </Card>
            ))}
          </div>
        );
      })()}

    </div>
  );
}
